import React, { useEffect, useMemo, useState } from 'react';
import { Table as AntTable, notification } from 'antd';
import DashboardModal from './DashboardModal';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';

type IgsCapsRow = {
    stateCode?: string;
    utilityName?: string;
    marketAnnualProductionCap?: number | string;
    marketSystemSizeCap?: number | string;
    marketYieldCap?: number | string;
};

export type IGSCapsModalProps = {
    open: boolean;
    onClose: () => void;
    opportunityId: string | number | null;
    theme: 'light' | 'dark';
};

const columns = [
    { title: 'State', dataIndex: 'stateCode', key: 'stateCode', width: 90 },
    { title: 'Utility', dataIndex: 'utilityName', key: 'utilityName', ellipsis: true },
    {
        title: 'Production Cap',
        dataIndex: 'marketAnnualProductionCap',
        key: 'marketAnnualProductionCap',
        width: 160,
        render: (v: unknown) => (v == null || v === '' ? '—' : `${v} KWH`),
    },
    {
        title: 'Size Cap',
        dataIndex: 'marketSystemSizeCap',
        key: 'marketSystemSizeCap',
        width: 120,
        render: (v: unknown) => (v == null || v === '' ? '—' : `${v} KW`),
    },
    { title: 'Yield Cap', dataIndex: 'marketYieldCap', key: 'marketYieldCap', width: 120, render: (v: unknown) => (v == null || v === '' ? '—' : String(v)) },
];

export const IGSCapsModal: React.FC<IGSCapsModalProps> = ({ open, onClose, opportunityId, theme }) => {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<IgsCapsRow[]>([]);
    const [noticeApi, noticeContextHolder] = notification.useNotification({ maxCount: 2 });

    const notifClass = useMemo(() => `obd-notification obd-theme-${theme}`, [theme]);

    useEffect(() => {
        if (!open) return;
        if (opportunityId == null || String(opportunityId).trim() === '') {
            setRows([]);
            noticeApi.warning({
                message: 'Missing opportunity',
                description: 'Unable to load IGS caps without an opportunity id.',
                className: notifClass,
            });
            return;
        }

        let cancelled = false;
        setLoading(true);
        setRows([]);

        (async () => {
            try {
                const res = await quoteServices.getIgsMarketCapData(opportunityId);
                const list = Array.isArray(res) ? (res as IgsCapsRow[]) : Array.isArray(res?.data) ? (res.data as IgsCapsRow[]) : res ? ([res] as IgsCapsRow[]) : [];
                if (!cancelled) setRows(list);
            } catch (e: unknown) {
                const msg = e instanceof Error ? e.message : 'Request failed.';
                if (!cancelled) {
                    noticeApi.error({
                        message: 'Could not load IGS caps',
                        description: msg,
                        className: notifClass,
                    });
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [open, opportunityId, noticeApi, notifClass]);

    return (
        <>
            {noticeContextHolder}
            <DashboardModal
                title="IGS: Market-based system constraints"
                open={open}
                onClose={onClose}
                footer={null}
                width="min(900px, 92vw)"
                theme={theme}
                maskClosable={false}
                destroyOnClose
                rootClassName={`obd-theme-${theme}`}
                zIndex={10070}
                loading={loading}
                loadingMessage="Loading…"
            >
                <div style={{ display: 'grid', gap: 10, padding: 16, minHeight: 220 }}>
                    <div>
                        <p style={{ marginBottom: 6 }}>There are limitations on the size, annual production, and yield for systems created via the IGS.</p>
                        <p style={{ marginBottom: 0 }}>The IGS Market-based system constraints below may apply to this opportunity.</p>
                    </div>
                    <div>
                        <AntTable<IgsCapsRow>
                            size="small"
                            loading={false}
                            columns={columns as any}
                            dataSource={rows.length ? [rows[0]] : []}
                            rowKey={(r, i) => String(r.utilityName ?? r.stateCode ?? i)}
                            pagination={false}
                        />
                    </div>
                </div>
            </DashboardModal>
        </>
    );
};

export default IGSCapsModal;

